import React, { Component } from 'react';
import { AuthContext } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import LoadingSpinner from '../components/LoadingSpinner';
import api from '../services/api';

class ManageUsers extends Component {
  static contextType = AuthContext;

  constructor(props) {
    super(props);
    this.state = {
      users: [],
      invitations: [],
      loading: true,
      searchQuery: '',
      filterAdmin: 'all',
      selectedUser: null,
      showEditModal: false,
      showDeleteModal: false,
      showInviteModal: false,
      inviteEmail: '',
      inviteAdmin: false,
      inviteError: null,
      editForm: {
        firstName: '',
        lastName: '',
        company: '',
        email: '',
        admin: false,
      },
      errors: {},
      actionLoading: false,
    };
  }

  async componentDidMount() {
    await this.fetchUsers();
    await this.fetchInvitations();
  }

  fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      this.setState({ users: response.data.users, loading: false });
    } catch (error) {
      console.error('Failed to fetch users:', error);
      this.setState({ loading: false });
    }
  };

  handleSearch = (e) => {
    this.setState({ searchQuery: e.target.value });
  };

  handleFilterChange = (filter) => {
    this.setState({ filterAdmin: filter });
  };

  openEditModal = (user) => {
    this.setState({
      selectedUser: user,
      showEditModal: true,
      editForm: {
        firstName: user.firstName,
        lastName: user.lastName,
        company: user.company || '',
        email: user.email,
        admin: user.admin,
      },
      errors: {},
      actionLoading: false,
    });
  };

  closeEditModal = () => {
    this.setState({
      showEditModal: false,
      selectedUser: null,
      editForm: {
        firstName: '',
        lastName: '',
        company: '',
        email: '',
        admin: false,
      },
      errors: {},
      actionLoading: false,
    });
  };

  openDeleteModal = (user) => {
    this.setState({
      selectedUser: user,
      showDeleteModal: true,
      actionLoading: false, // ✅ Add this
    });
  };
  
  closeDeleteModal = () => {
    this.setState({
      showDeleteModal: false,
      selectedUser: null,
      actionLoading: false, // ✅ Add this
    });
  };

  handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState({
      editForm: {
        ...this.state.editForm,
        [name]: type === 'checkbox' ? checked : value,
      },
      errors: {},
    });
  };

  handleUpdateUser = async (e) => {
    e.preventDefault();
    const { selectedUser, editForm } = this.state;
  
    this.setState({ actionLoading: true });
  
    try {
      await api.put(`/users/${selectedUser.id}`, editForm);
      await this.fetchUsers();
      this.closeEditModal(); // This now resets actionLoading
    } catch (error) {
      this.setState({
        errors: { general: error.response?.data?.message || 'Failed to update user' },
        actionLoading: false, // ✅ This is already correct
      });
    }
  };

  handleDeleteUser = async () => {
    const { selectedUser } = this.state;
    this.setState({ actionLoading: true });

    try {
      await api.delete(`/users/${selectedUser.id}`);
      await this.fetchUsers();
      this.closeDeleteModal();
      this.setState({ actionLoading: false });
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete user');
      this.setState({ actionLoading: false });
    }
  };

  toggleAdminStatus = async (user) => {
    if (window.confirm(`Are you sure you want to ${user.admin ? 'remove admin access from' : 'make'} ${user.firstName} ${user.lastName} ${user.admin ? '?' : 'an admin?'}`)) {
      try {
        await api.patch(`/users/${user.id}/admin`, { admin: !user.admin });
        await this.fetchUsers();
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to update admin status');
      }
    }
  };

  getFilteredUsers = () => {
    const { users, searchQuery, filterAdmin } = this.state;

    let filtered = users;

    // Filter by admin status
    if (filterAdmin === 'admin') {
      filtered = filtered.filter(u => u.admin);
    } else if (filterAdmin === 'regular') {
      filtered = filtered.filter(u => !u.admin);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(u =>
        u.firstName.toLowerCase().includes(query) ||
        u.lastName.toLowerCase().includes(query) ||
        u.email.toLowerCase().includes(query) ||
        (u.company && u.company.toLowerCase().includes(query))
      );
    }

    return filtered;
  };

  formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  fetchInvitations = async () => {
    try {
      const response = await api.get('/invitations');
      this.setState({ invitations: response.data.invitations });
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
    }
  };
  
  openInviteModal = () => {
    this.setState({
      showInviteModal: true,
      inviteEmail: '',
      inviteAdmin: false,
      inviteError: null,
    });
  };
  
  closeInviteModal = () => {
    this.setState({
      showInviteModal: false,
      inviteEmail: '',
      inviteAdmin: false,
      inviteError: null,
    });
  };
  
  handleInviteChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState({
      [name]: type === 'checkbox' ? checked : value,
      inviteError: null,
    });
  };
  
  handleSendInvite = async (e) => {
    e.preventDefault();
    const { inviteEmail, inviteAdmin } = this.state;
  
    if (!inviteEmail || !/\S+@\S+\.\S+/.test(inviteEmail)) {
      this.setState({ inviteError: 'Please enter a valid email address' });
      return;
    }
  
    this.setState({ actionLoading: true, inviteError: null });
  
    try {
      await api.post('/invitations', {
        email: inviteEmail,
        admin: inviteAdmin,
      });
  
      await this.fetchInvitations();
      this.closeInviteModal();
      this.setState({ actionLoading: false });
    } catch (error) {
      this.setState({
        inviteError: error.response?.data?.message || 'Failed to send invitation',
        actionLoading: false,
      });
    }
  };
  
  handleResendInvitation = async (invitationId) => {
    if (window.confirm('Resend this invitation?')) {
      try {
        await api.post(`/invitations/${invitationId}/resend`);
        await this.fetchInvitations();
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to resend invitation');
      }
    }
  };
  
  handleDeleteInvitation = async (invitationId) => {
    if (window.confirm('Delete this invitation?')) {
      try {
        await api.delete(`/invitations/${invitationId}`);
        await this.fetchInvitations();
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to delete invitation');
      }
    }
  };

  render() {
    const { 
      users,
      invitations, // ✅ Add this
      loading, 
      searchQuery, 
      filterAdmin, 
      selectedUser, 
      showEditModal, 
      showDeleteModal,
      showInviteModal, // ✅ Add this
      inviteEmail, // ✅ Add this
      inviteAdmin, // ✅ Add this
      inviteError, // ✅ Add this
      editForm, 
      errors, 
      actionLoading 
    } = this.state;
    const { user: currentUser } = this.context;
    const filteredUsers = this.getFilteredUsers();

    if (loading) {
      return (
        <div className="min-h-screen bg-slate-50">
          <Navbar />
          <div className="flex items-center justify-center h-96">
            <LoadingSpinner size="large" />
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">User Management</h1>
              <p className="text-slate-600 mt-1">Manage user accounts and permissions</p>
            </div>
            
            {/* ✅ Add Invite Button */}
            <button
              onClick={this.openInviteModal}
              className="btn-primary flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Invite User</span>
            </button>
          </div>

          {/* Filters and Search */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
            <div className="p-4 border-b border-slate-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
                {/* Filter Tabs */}
                <div className="flex space-x-1 bg-slate-100 rounded-lg p-1">
                  <button
                    onClick={() => this.handleFilterChange('all')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                      filterAdmin === 'all'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    All Users ({this.state.users.length})
                  </button>
                  <button
                    onClick={() => this.handleFilterChange('admin')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                      filterAdmin === 'admin'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Admins ({this.state.users.filter(u => u.admin).length})
                  </button>
                  <button
                    onClick={() => this.handleFilterChange('regular')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                      filterAdmin === 'regular'
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Regular ({this.state.users.filter(u => !u.admin).length})
                  </button>
                </div>

                {/* Search */}
                <div className="relative w-full sm:w-64">
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={this.handleSearch}
                    className="w-full px-4 py-2 pl-10 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                  <svg className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Pending Invitations */}
            {this.state.invitations.filter(inv => inv.status === 'pending').length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
                <div className="px-6 py-4 border-b border-slate-200">
                  <h2 className="text-lg font-semibold text-slate-900">Pending Invitations</h2>
                </div>
                <div className="divide-y divide-slate-200">
                  {this.state.invitations
                    .filter(inv => inv.status === 'pending')
                    .map((invitation) => (
                      <div key={invitation.id} className="px-6 py-4 flex items-center justify-between hover:bg-slate-50">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-900">{invitation.email}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            Invited by {invitation.inviterName} • Expires {new Date(invitation.expiresAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          {invitation.admin && (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                              Admin
                            </span>
                          )}
                          <button
                            onClick={() => this.handleResendInvitation(invitation.id)}
                            className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                            title="Resend"
                          >
                            Resend
                          </button>
                          <button
                            onClick={() => this.handleDeleteInvitation(invitation.id)}
                            className="text-red-600 hover:text-red-900 text-sm font-medium"
                            title="Delete"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Users Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Joined</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center">
                          <svg className="w-12 h-12 text-slate-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                          <p className="text-slate-600">No users found</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-slate-50 transition">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center mr-3">
                              <span className="text-sm font-semibold text-white">
                                {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <div className="flex items-center space-x-2">
                                <p className="text-sm font-medium text-slate-900">
                                  {user.firstName} {user.lastName}
                                </p>
                                {user.id === currentUser.id && (
                                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">You</span>
                                )}
                              </div>
                              <p className="text-sm text-slate-500">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="text-sm text-slate-900">{user.company || '-'}</p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {user.admin ? (
                            <span className="px-3 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                              Admin
                            </span>
                          ) : (
                            <span className="px-3 py-1 text-xs font-semibold rounded-full bg-slate-100 text-slate-800">
                              User
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                          {this.formatDate(user.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {user.id !== currentUser.id && (
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => this.toggleAdminStatus(user)}
                                className="text-purple-600 hover:text-purple-900"
                                title={user.admin ? 'Remove admin' : 'Make admin'}
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => this.openEditModal(user)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Edit user"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => this.openDeleteModal(user)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete user"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>

        {/* Edit User Modal */}
        {showEditModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Edit User</h2>
              
              {errors.general && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {errors.general}
                </div>
              )}

              <form onSubmit={this.handleUpdateUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">First Name</label>
                    <input
                      type="text"
                      name="firstName"
                      value={editForm.firstName}
                      onChange={this.handleEditFormChange}
                      className="input-field"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      name="lastName"
                      value={editForm.lastName}
                      onChange={this.handleEditFormChange}
                      className="input-field"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Company</label>
                  <input
                    type="text"
                    name="company"
                    value={editForm.company}
                    onChange={this.handleEditFormChange}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={editForm.email}
                    onChange={this.handleEditFormChange}
                    className="input-field"
                    required
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="admin"
                    checked={editForm.admin}
                    onChange={this.handleEditFormChange}
                    className="w-4 h-4 text-teal-600 border-slate-300 rounded focus:ring-teal-500"
                  />
                  <label className="ml-2 text-sm text-slate-700">Admin privileges</label>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={this.closeEditModal}
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg font-medium text-slate-700 hover:bg-slate-50 transition"
                    disabled={actionLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 btn-primary"
                    disabled={actionLoading}
                  >
                    {actionLoading ? <LoadingSpinner size="small" /> : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete User Modal */}
        {showDeleteModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              
              <h2 className="text-2xl font-bold text-slate-900 mb-2 text-center">Delete User?</h2>
              <p className="text-slate-600 text-center mb-6">
                Are you sure you want to delete <strong>{selectedUser.firstName} {selectedUser.lastName}</strong>? This action cannot be undone.
              </p>

              <div className="flex space-x-3">
                <button
                  onClick={this.closeDeleteModal}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg font-medium text-slate-700 hover:bg-slate-50 transition"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={this.handleDeleteUser}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition"
                  disabled={actionLoading}
                >
                  {actionLoading ? <LoadingSpinner size="small" /> : 'Delete User'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Invite User Modal */}
        {showInviteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Invite User</h2>

              {inviteError && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {inviteError}
                </div>
              )}

              <form onSubmit={this.handleSendInvite} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="inviteEmail"
                    value={inviteEmail}
                    onChange={this.handleInviteChange}
                    className="input-field"
                    placeholder="colleague@company.com"
                    required
                    autoFocus
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="inviteAdmin"
                    checked={inviteAdmin}
                    onChange={this.handleInviteChange}
                    className="w-4 h-4 text-teal-600 border-slate-300 rounded focus:ring-teal-500"
                  />
                  <label className="ml-2 text-sm text-slate-700">
                    Grant admin privileges
                  </label>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    An invitation email will be sent to this address. The invitation expires in 48 hours.
                  </p>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={this.closeInviteModal}
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg font-medium text-slate-700 hover:bg-slate-50 transition"
                    disabled={actionLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 btn-primary"
                    disabled={actionLoading}
                  >
                    {actionLoading ? <LoadingSpinner size="small" /> : 'Send Invitation'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default ManageUsers;